import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
	addSpecialRegistrationRequest,
	deleteScheduleTerminationRequest,
	getSpecialRegistrationsRequest,
	updateSpecialRegistrationRequest,
} from 'redux/reducers/emManagement/reducer';
import { useEffect, useState } from 'react';
import {
	getSpecialRegistrations,
	getSpecialRegistrationsLoading,
} from 'redux/reducers/emManagement/selectors';
import { TEmAdditionalService, TSpecialRegistrationItem } from 'redux/reducers/emManagement/types';
import { ItemWithId, TTableStructure } from 'ui/EasTable/types';
import { Box, Button, Typography } from '@mui/material';
import { PAGE_SIZE } from 'services/constants/env';
import { ESpecialRegistrationSchemeType } from 'redux/reducers/emManagement/constants';
import { tableStructure } from './tableStructure';
import { TBoxStructure } from '../../EasParamsBox/types';
import { EditParamsDialog } from './EditParamsDialog';
import { TerminateDialog } from './TerminateDialog';
import { DeleteDialog } from 'ui/DeleteDialog';
import { ESpecialRegistrationAction } from './constants';

export const EmSpecialRegistrations = () => {
	const { emId } = useParams();
	const dispatch = useDispatch();
	const specialRegistrations = useSelector(getSpecialRegistrations);
	const specialRegistrationsLoading = useSelector(getSpecialRegistrationsLoading);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [idToEdit, setIdToEdit] = useState<number | null>(null);
	const [isTerminateDialogOpen, setIsTerminateDialogOpen] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	// useEffect(() => {
	// 	dispatch(getSpecialRegistrationsRequest({ emId: Number(emId) }));
	// }, [emId, dispatch]);

	const handleEditClose = () => {
		setIsEditDialogOpen(false);
		setIdToEdit(null);
	};

	const handleTerminateClose = () => {
		setIsTerminateDialogOpen(false);
		setIdToEdit(null);
	};

	const handleSave = (data: Record<string, any>) => {
		if (idToEdit === null) {
			dispatch(
				addSpecialRegistrationRequest({
					data: {
						specialSchemeType: data.specialSchemeType,
						schemeNumber: data.schemeNumber,
						registrationRequestDate: data.registrationRequestDate,
						commencementDate: data.commencementDate,
						countryId: data.countryId,
						emNonEuVatId: 1,
					},
					emId: Number(emId),
				}),
			);
		} else if (idToEdit) {
			if (
				!specialRegistrations ||
				specialRegistrations.data.length <= 0 ||
				!specialRegistrations.data.find((item) => item.id === idToEdit)?.specialSchemeType
			) {
				return;
			}

			dispatch(
				updateSpecialRegistrationRequest({
					data: {
						specialSchemeType: specialRegistrations?.data?.find((item) => item.id === idToEdit)
							?.specialSchemeType as ESpecialRegistrationSchemeType,
						schemeNumber: data.schemeNumber,
						registrationRequestDate: data.registrationRequestDate,
						commencementDate: data.commencementDate,
						countryId: data.countryId,
						emNonEuVatId:
							specialRegistrations?.data?.find((item) => item.id === idToEdit)?.emNonEuVatId ?? 1,
					},
					emId: Number(emId),
				}),
			);
		}
	};

	const tableStructureToBoxStructure = (
		tableStruc: TTableStructure<keyof TSpecialRegistrationItem>[],
	) => {
		const boxStructure: TBoxStructure<TSpecialRegistrationItem> = {
			body: [],
			header: [],
			title: idToEdit ? 'Edit Special Registration' : 'Add Special Registration',
		};
		tableStruc.forEach((item: TTableStructure<keyof TSpecialRegistrationItem>) => {
			if (item?.editable === true) {
				boxStructure.body.push({
					name: item.name,
					selector: item.selector,
					type: item.type,
					editable: true,
					filter: item?.filter,
				});
			}
		});
		return boxStructure;
	};

	const handleAction = (id: number, typeOfDialog: string) => {
		switch (typeOfDialog) {
			case ESpecialRegistrationAction.EDIT:
				setIdToEdit(id);
				setIsEditDialogOpen(true);
				break;
			case ESpecialRegistrationAction.TERMINATE:
				setIdToEdit(id);
				setIsTerminateDialogOpen(true);
				break;
			case ESpecialRegistrationAction.CANCEL_TERMINATION:
				setIdToEdit(id);
				setOpenDeleteDialog(true);
				break;
			default:
				break;
		}
	};

	const handleDelete = () => {
		if (!idToEdit) return;
		if (!specialRegistrations?.data?.find((item) => item.id === idToEdit)?.terminationId) return;
		dispatch(
			deleteScheduleTerminationRequest({
				id: specialRegistrations?.data?.find((item) => item.id === idToEdit)
					?.terminationId as number,
				emId: Number(emId),
			}),
		);
		setOpenDeleteDialog(false);
	};

	return (
		<Box>
			<EasTableWithoutFilter<TSpecialRegistrationItem & ItemWithId>
				tableStructure={tableStructure}
				items={specialRegistrations}
				itemsIsLoading={specialRegistrationsLoading}
				handleAction={handleAction}
				skeletonRows={[1]}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Special VAT Schemes
					</Typography>
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={() => {
							setIdToEdit(null);
							setIsEditDialogOpen(true);
						}}
					>
						Add Special VAT Scheme
					</Button>
				</Box>
			</EasTableWithoutFilter>
			<EditParamsDialog<TSpecialRegistrationItem>
				open={isEditDialogOpen}
				setClose={handleEditClose}
				data={
					idToEdit
						? (specialRegistrations?.data?.find((item) => item.id === idToEdit) ?? null)
						: null
				}
				boxStructure={tableStructureToBoxStructure(tableStructure)}
				onSave={handleSave}
				title={idToEdit ? 'Edit Special Registration' : 'Add Special Registration'}
				isEdit={!!idToEdit}
			/>
			<TerminateDialog
				open={isTerminateDialogOpen}
				setClose={handleTerminateClose}
				data={
					idToEdit
						? (specialRegistrations?.data?.find((item) => item.id === idToEdit) ?? null)
						: null
				}
			/>
			<DeleteDialog
				open={openDeleteDialog}
				setClose={() => setOpenDeleteDialog(false)}
				handleDelete={handleDelete}
				text="Are you sure you want to cancel this schedule termination?"
				buttonName="Cancel Schedule Termination"
			/>
		</Box>
	);
};
